import React, { useEffect, useRef } from 'react';
import type { ExpressStockOption, DeliveryOptionRow } from 'data-types';
import { resolveExpressBlockState, expressDefaultTierABTest, expressTestVariantLabel, applyProviderAvailability } from './expressDeliveryUtils';
import { SHIPPING_EVENTS } from '../../../v2/ShippingV2/utils/shippingTrackingEvents';
import CountdownTimer from './CountdownTimer';
import * as translator from '../../../../utils/translator';
import { MainTracker } from '../../../../PixelTrackers/MainTracker';

interface ExpressDeliveryBlockProps {
  zipcode: string;
  expressStockOptions: ExpressStockOption[];
  customerID?: number | string;
  selectedTier: 'uber_2h' | 'same_day' | null;
  onTierSelect: (tier: 'uber_2h' | 'same_day') => void;
  onClearZipcode: () => void;
  onFallbackShipping: () => void;
  onFallbackPickup: () => void;
  language: string;
  onCountdownExpired?: () => void;
  // HL-10272: provider-level availability. When a provider is closed (e.g. Uber at midnight,
  // its pharmacies/doctors are outside the current-time window) its option row is disabled even
  // if the ZIP covers it. undefined = treat as open (backward-compatible).
  isExpressUberFlow?: boolean;
  isExpressUrbifyFlow?: boolean;
  titleClassName?: string;
  shippingMethodClassName?: string;
  collectionMethodClassName?: string;
}

type OptionEntry = {
  tier: 'uber_2h' | 'same_day';
  option: DeliveryOptionRow;
};

const RadioUnselected: React.FC = () => (
  <div className="rounded-full flex items-center justify-center express-radio-unselected" />
);

const RadioSelected: React.FC = () => (
  <div className="rounded-full flex items-center justify-center express-radio-selected">
    <span className="express-radio-selected-fill" />
  </div>
);

const getDisabledSectionHeader = (disabledOptions: OptionEntry[]): string | null => {
  const hasCutoffDisabled = disabledOptions.some(
    o => o.option.disabledReason === 'same_day_disabled_cutoff'
  );
  if (hasCutoffDisabled) return 'not_available_after_cutoff_header';

  const hasAreaDisabled = disabledOptions.some(
    o => o.option.disabledReason === 'uber_disabled_area' || o.option.disabledReason === 'same_day_disabled_area'
  );
  if (hasAreaDisabled) return 'not_available_in_area_header';

  const isDisabledDueToClosedHours = disabledOptions.some(
    o => o.option.disabledReason === 'uber_disabled_closed' || o.option.disabledReason === 'same_day_disabled_closed'
  );
  if (isDisabledDueToClosedHours) return 'not_available_right_now_header';

  return null;
};

const ExpressDeliveryBlock: React.FC<ExpressDeliveryBlockProps> = ({
  zipcode,
  expressStockOptions,
  selectedTier,
  onTierSelect,
  onClearZipcode,
  onFallbackShipping,
  onFallbackPickup,
  language,
  onCountdownExpired,
  isExpressUberFlow,
  isExpressUrbifyFlow,
  titleClassName,
  shippingMethodClassName,
  collectionMethodClassName
}) => {
  const resolution = resolveExpressBlockState(expressStockOptions);
  const { state, countdown } = resolution;
  // Fold provider-level availability into the ZIP-derived rows: uber_2h = Uber, same_day = Urbify.
  let uberOption = applyProviderAvailability(resolution.uberOption, isExpressUberFlow, 'uber_disabled_closed');
  let sameDayOption = applyProviderAvailability(resolution.sameDayOption, isExpressUrbifyFlow, 'same_day_disabled_closed');

  // Re-evaluate isDefault after provider-level closure: if both are enabled, the AB test wins;
  // if only one is enabled, it becomes the default regardless of the AB test variant in storage.
  if (uberOption && sameDayOption) {

    const abVariant = expressDefaultTierABTest.getVariant();
    const uberEnabled = !!uberOption.isEnabled;
    const sameDayEnabled = !!sameDayOption.isEnabled;

    if (uberEnabled && sameDayEnabled) {
      
      uberOption = { ...uberOption, isDefault: abVariant === 'uber_2h' };
      sameDayOption = { ...sameDayOption, isDefault: abVariant === 'same_day' };

    } else if (uberEnabled && !sameDayEnabled) {

      uberOption = { ...uberOption, isDefault: true };
      sameDayOption = { ...sameDayOption, isDefault: false };

    } else if (!uberEnabled && sameDayEnabled) {

      uberOption = { ...uberOption, isDefault: false };
      sameDayOption = { ...sameDayOption, isDefault: true };

    }
  } else if (uberOption && !sameDayOption) {
    
    uberOption = { ...uberOption, isDefault: !!uberOption.isEnabled };
  
  } else if (sameDayOption && !uberOption) {
    
    sameDayOption = { ...sameDayOption, isDefault: !!sameDayOption.isEnabled };
  
  }
  
  const hasAutoSelected = useRef(false);
  const hasTrackedABTest = useRef(false);

  useEffect(() => {
    if (!hasTrackedABTest.current && state === 'bothActive') {
      hasTrackedABTest.current = true;
      MainTracker.track('custom', SHIPPING_EVENTS.EXPRESS_AB_TEST, {
        Variant: expressTestVariantLabel(expressDefaultTierABTest.getVariant()),
      });
    }
  }, [state]);

  useEffect(() => {
    if (hasAutoSelected.current) return;
    if (selectedTier !== null) return;

    if (uberOption?.isDefault && uberOption.isEnabled) {
      onTierSelect('uber_2h');
      hasAutoSelected.current = true;
    } else if (sameDayOption?.isDefault && sameDayOption.isEnabled) {
      onTierSelect('same_day');
      hasAutoSelected.current = true;
    } else if (uberOption?.isEnabled) {
      onTierSelect('uber_2h');
      hasAutoSelected.current = true;
    } else if (sameDayOption?.isEnabled) {
      onTierSelect('same_day');
      hasAutoSelected.current = true;
    }
  }, []);

  // HL-10272: also fall back when the provider flags leave no bookable option (e.g. both closed).
  const hasEnabledOption = !!uberOption?.isEnabled || !!sameDayOption?.isEnabled;
  const isFallback = state === 'hidden' || state === 'noneAvailable' || state === 'sameDayMissed' || !hasEnabledOption;
  const fallbackMessage = resolution.fallbackMessage ?? 'express_delivery_not_available_title';

  if (isFallback) {
    const isSameDayMissed = state === 'sameDayMissed';

    const fallbackTitle = translator
      .translate(language, "choose_treatment", `shipping_and_service_selection/${fallbackMessage}`)
      ?.replace('<cutoff>', countdown?.cutOffTime || '');

    const fallbackDescriptionKey = isSameDayMissed
      ? 'same_day_missed_description'
      : 'express_delivery_not_available_description';
    
    const shippingButtonKey = isSameDayMissed ? 'shipping_same_day_cutoff' : 'shipping';

    return (
      <div className="w-full flex flex-col items-center justify-center mt-2 mb-2">
        <p className={`${titleClassName || " "} text-xl font-roboto font-bold text-center`}>
          {fallbackTitle}
        </p>
        <p className="text-[#98A0AF] font-roboto text-xs text-center my-2">
          {translator.translate(language, "choose_treatment", `shipping_and_service_selection/${fallbackDescriptionKey}`)}
        </p>
        <div className="flex flex-row w-full gap-3 mt-1">
          <button
            className={`${shippingMethodClassName || " "} flex items-center justify-center text-[#269C6E] text-lg font-semibold gap-2 h-14 w-full cursor-pointer bg-white border-1 border-solid border-[#269C6E] rounded-md p-3 font-roboto hover:opacity-80`}
            onClick={onFallbackShipping}
          >
            {translator.translate(language, "choose_treatment", `shipping_and_service_selection/${shippingButtonKey}`)}
          </button>
          <button
            className={`${collectionMethodClassName || " "} flex items-center justify-center text-[#269C6E] text-lg font-semibold gap-2 h-14 w-full cursor-pointer bg-white border-1 border-solid border-[#269C6E] rounded-md p-3 font-roboto hover:opacity-80`}
            onClick={onFallbackPickup}
          >
            {translator.translate(language, "choose_treatment", "shipping_and_service_selection/pickup")}
          </button>
        </div>
      </div>
    );
  }

  const formatPrice = (cents: number): string => {
    const euros = cents / 100;
    return euros.toFixed(2).replace('.', ',') + ' €';
  };

  const handleOptionClick = (tier: 'uber_2h' | 'same_day', isEnabled: boolean) => {
    if (!isEnabled) return;
    onTierSelect(tier);
    MainTracker.track('custom', 'Express_Tier_Selected', {
      tier,
      zipcode,
    });
  };

  const allOptions: OptionEntry[] = [];
  if (uberOption) allOptions.push({ tier: 'uber_2h', option: uberOption });
  if (sameDayOption) allOptions.push({ tier: 'same_day', option: sameDayOption });

  const enabledOptions = allOptions.filter(o => o.option.isEnabled);
  const disabledOptions = allOptions.filter(o => !o.option.isEnabled);
  const disabledHeaderKey = disabledOptions.length > 0 ? getDisabledSectionHeader(disabledOptions) : null;

  const renderOptionRow = ({ tier, option }: OptionEntry): React.ReactNode => {
    const isSelected = selectedTier === tier && option.isEnabled;
    const isSameDay = tier === 'same_day';

    return (
      <button
        key={tier}
        type="button"
        className={`express-option-row ${isSelected ? 'express-option-row--selected' : ''} ${!option.isEnabled ? 'express-option-row--disabled' : ''}`}
        onClick={() => handleOptionClick(tier, option.isEnabled)}
        disabled={!option.isEnabled}
      >
        <div className="express-option-row-main">
          <div className="express-radio-slot">
            {isSelected ? <RadioSelected /> : <RadioUnselected />}
          </div>
          <div className="flex flex-col min-w-0 flex-1">
            <div className="express-option-header">
              <span className={`express-option-label ${!option.isEnabled ? 'express-option-label--disabled' : ''}`}>
                {translator.translate(language, "choose_treatment", `shipping_and_service_selection/${option.label}`)}
              </span>
              {option.priceEurCents > 0 && (
                <span className={`express-option-price ${!option.isEnabled ? 'express-option-price--disabled' : ''}`}>
                  {formatPrice(option.priceEurCents)}
                </span>
              )}
            </div>
            {isSameDay && sameDayOption?.sublabel && (
              <span className="express-option-sublabel">
                {translator.translate(language, "choose_treatment", `shipping_and_service_selection/${sameDayOption.sublabel}`)}
              </span>
            )}
            {isSameDay &&
              option.isEnabled &&
              countdown &&
              countdown.countdownSeconds !== null &&
              !countdown.isPassed && (
                <CountdownTimer
                  cutoffTime={countdown.cutOffTime}
                  onExpired={onCountdownExpired || (() => {})}
                  language={language}
                  variant="sameDayInline"
                />
              )}
          </div>
        </div>
      </button>
    );
  };

  return (
    <div className="w-full flex flex-col gap-2">
      {/* Location chip */}
      <div className="express-location-chip">
        <span className="express-location-chip-label">
          {translator.translate(language, "choose_treatment", "shipping_and_service_selection/express_available_in")}
        </span>
        <div className="express-location-chip-value">
          <span>{zipcode}</span>
          <button
            type="button"
            className="express-location-chip-clear"
            onClick={onClearZipcode}
            aria-label="Clear zipcode"
          >
            ×
          </button>
        </div>
      </div>

      {enabledOptions.map(renderOptionRow)}

      {disabledOptions.length > 0 && disabledHeaderKey && (
        <span className="express-disabled-section-header">
          {translator.translate(language, "choose_treatment", `shipping_and_service_selection/${disabledHeaderKey}`)}
        </span>
      )}

      {disabledOptions.map(renderOptionRow)}
    </div>
  );
};

export default ExpressDeliveryBlock;
